import { useEffect, useRef, useState } from 'react';

import { testPasswordStrength } from '../../../helpers/passwordStrength';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import PasswordValidationTooltip from '../../auth/form/PasswordValidationTooltip';
import AuthFormTermsLink from '../../auth/form/TermsLink';
import Button from '../../generic/Button/Button';
import LabeledPasswordInput from '../../generic/Forms/LabeledPasswordInput';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import { icons } from '../../generic/Icon';
import PasswordMeter from '../../generic/PasswordMeter';

import styles from '../Login.module.less';
import registerStyles from '../Register.module.less';

// Wait for a typing pause before flagging a confirm-password mismatch, so the warning doesn't flash on every
// keystroke while the confirmation is still being typed.
const PASSWORD_MATCH_DEBOUNCE_MS = 400;

// How many of the four character categories the account password policy requires. Kept in step with
// `hasSufficientComplexity`, which the registration endpoint and the `User` model both enforce.
const MIN_PASSWORD_CATEGORIES = 3;

export type SetPasswordStepProps = {
  /** "Sign up", or "Sign up and join {org}" for an invited signup. */
  heading: string;
  /** The address the account is being created for, shown read-only. */
  email: string;
  /** Inline error from a failed registration (e.g. a conflicting email). */
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (password: string) => void;
  onBack: () => void;
};

/**
 * Collects a *new* password: a brand-new email, an invited signup, or a returning all-SSO email creating its
 * first password. Owns the whole password-entry concern — both fields, the strength meter and rules, and the
 * debounced mismatch warning — so the flow above it deals only in "a password was submitted". Because the
 * entries live here, leaving the step discards them rather than carrying them to another account.
 *
 * (The returning-email variant that confirms an *existing* password is {@link ConfirmPasswordStep}, which has
 * no strength meter — that password is verified server-side against a sibling, not scored.)
 */
export const SetPasswordStep = ({ heading, email, error, isSubmitting, onSubmit, onBack }: SetPasswordStepProps) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);
  const { debouncedValue: debouncedPassword } = useDebouncedValue(password, PASSWORD_MATCH_DEBOUNCE_MS);
  const { debouncedValue: debouncedConfirmPassword } = useDebouncedValue(confirmPassword, PASSWORD_MATCH_DEBOUNCE_MS);

  // The email field is read-only, so land focus on the password when the flow arrives here.
  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const passwordStrength = testPasswordStrength(password);
  // Mirror the server's floor (`hasSufficientComplexity` in serverutil/passwordPolicy): length, plus at least
  // three of the four character categories. Gating on all four would refuse passwords the API accepts —
  // `Hunter123` is valid server-side — while gating on nothing (V1's behaviour) spends a round trip to be
  // told so. The rules checklist below still lists all four, as the same advisory nudge V1 shows.
  const satisfiedCategories = [
    passwordStrength.hasLowerCase,
    passwordStrength.hasUpperCase,
    passwordStrength.hasNumber,
    passwordStrength.hasSymbol,
  ].filter(Boolean).length;
  const isPasswordValid = passwordStrength.isLongEnough && satisfiedCategories >= MIN_PASSWORD_CATEGORIES;
  const arePasswordsEqual = password === confirmPassword;
  const canSubmit = isPasswordValid && arePasswordsEqual;
  // Debounce the *appearance* of the mismatch warning: surface it once typing settles in *either* field on an
  // unequal pair (debouncing both fields, not just the confirmation, keeps it from flashing on every keystroke
  // when the original password is edited afterward), but let the live comparison clear it the moment they match.
  const isPasswordMismatch =
    confirmPassword.length > 0
    && !arePasswordsEqual
    && debouncedPassword === password
    && debouncedConfirmPassword === confirmPassword;

  const handleSubmit = () => {
    // Enter reaches the form's onSubmit directly, bypassing the disabled button, so the in-flight guard has to
    // live here too (mirrors ConfirmPasswordStep and SignupEmailStep).
    if (!canSubmit || isSubmitting) {
      return;
    }
    onSubmit(password);
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>{heading}</h1>
      <div className={styles.fieldGroup}>
        <LabeledTextInput label="Email" name="email" value={email} isReadOnly />
        <LabeledPasswordInput ref={passwordRef} label="Password" value={password} onChange={setPassword} isRequired />
        <LabeledPasswordInput
          label="Confirm password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          isRequired
        />
      </div>
      {isPasswordMismatch && (
        <p role="alert" className={styles.error}>
          Passwords do not match.
        </p>
      )}
      <div className={registerStyles.meter}>
        <PasswordMeter strength={passwordStrength.label} />
      </div>
      <div className={registerStyles.rules}>
        <PasswordValidationTooltip strength={passwordStrength} />
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {/* A submit button lets Enter submit the form; the signup runs in the form's onSubmit, so onClick is unused. */}
      <Button isFullWidth size="large" isSubmitButton isDisabled={!canSubmit || isSubmitting} onClick={() => undefined}>
        Sign up
      </Button>
      <Button
        type="anchor"
        size="small"
        icon={icons.arrowLeft}
        className={styles.backLink}
        isDisabled={isSubmitting}
        onClick={onBack}
      >
        Use a different email
      </Button>
      <div className={registerStyles.terms}>
        <AuthFormTermsLink />
      </div>
    </form>
  );
};
